import {
  Clipboard,
  Download,
  ExternalLink,
  FileDown,
  FileText,
  Link2,
  Link2Off,
  Loader2,
  RotateCcw,
  Share2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ActionBar } from "@/components/layout/action-bar";
import { features } from "@/lib/features";

interface WorkspaceHeaderActionsProps {
  canEdit: boolean;
  isRerunPending: boolean;
  onRerun: () => void;
  onDownloadPdf: () => void;
  onDownloadHtml: () => void;
  onSharePublicLink: () => void;
  onRevokeShareLink: () => void;
  onCopyLink: () => void;
  onOpenReportDialog?: () => void;
  onCopyPrompt?: () => void;
  isCopyingPrompt?: boolean;
  canCopyPrompt: boolean;
  hasHtmlReport: boolean;
  isSharing: boolean;
  shareInfo: { enabled: boolean; shareUrl: string | null } | null;
}

export function WorkspaceHeaderActions({
  canEdit,
  isRerunPending,
  onRerun,
  onDownloadPdf,
  onDownloadHtml,
  onSharePublicLink,
  onRevokeShareLink,
  onCopyLink,
  onOpenReportDialog,
  onCopyPrompt,
  isCopyingPrompt,
  canCopyPrompt,
  hasHtmlReport,
  isSharing,
  shareInfo,
}: WorkspaceHeaderActionsProps) {
  return (
    <ActionBar
      primary={
        features.liveWalkthrough && canEdit ? (
          <Button disabled={isRerunPending} onClick={onRerun}>
            {isRerunPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="mr-2 h-4 w-4" />
            )}
            Re-run
          </Button>
        ) : undefined
      }
      secondary={[
        <DropdownMenu key="share">
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <Share2 className="mr-2 h-4 w-4" />
              Share & Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={onDownloadPdf}>
              <FileDown className="mr-2 h-4 w-4" />
              Download PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDownloadHtml}>
              <Download className="mr-2 h-4 w-4" />
              Download HTML report
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onSharePublicLink} disabled={isSharing}>
              <Link2 className="mr-2 h-4 w-4" />
              Copy shareable link
            </DropdownMenuItem>
            {shareInfo?.enabled && shareInfo?.shareUrl && (
              <>
                <DropdownMenuItem
                  onClick={() => window.open(shareInfo.shareUrl!, "_blank")}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open public report
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={onRevokeShareLink}
                  disabled={isSharing}
                  className="text-destructive focus:text-destructive"
                >
                  <Link2Off className="mr-2 h-4 w-4" />
                  Revoke public link
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onCopyLink}>
              <Share2 className="mr-2 h-4 w-4" />
              Copy workspace link
            </DropdownMenuItem>
            {hasHtmlReport && onOpenReportDialog && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onOpenReportDialog}>
                  <FileText className="mr-2 h-4 w-4" />
                  Open report dialog
                </DropdownMenuItem>
              </>
            )}
            {canCopyPrompt && onCopyPrompt && (
              <DropdownMenuItem
                onClick={onCopyPrompt}
                disabled={isCopyingPrompt}
              >
                <Clipboard className="mr-2 h-4 w-4" />
                Copy as prompt
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>,
      ]}
    />
  );
}
